import gql from 'graphql-tag';
import type { DocumentNode } from 'graphql';

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { isEmptyObject } from '../utils/isEmptyObject';

import { useLocalStorage } from '@zapier/common-hooks';
import { useMediaProps } from '@zapier/responsive-utils';
import { useTopNav } from './useTopNav';
import type { TopBarLinksType } from '../constants';

import type { UseQuery } from '../types';
import {
  UNIVERSAL_TOPBAR_CONDENSED_HEIGHT,
  UNIVERSAL_TOPBAR_HEIGHT,
  UNIVERSAL_SIDEBAR_COLLAPSED_WIDTH,
  UNIVERSAL_SIDEBAR_WIDTH,
} from '../constants';
import { readCookie } from '@zapier/cookies';

type UniversalNavContext = {
  canForceSidebarOpen: boolean;
  canTopbarFitBackToAppButton: boolean;
  canTopbarFitLinks: boolean;
  currentAccountId?: number;
  currentUrl: string;
  isLoadingLoggedIn: boolean;
  isLoggedIn: boolean;
  isLoadingTopNav: boolean;
  isOnAppPage: boolean;
  isSearchFullWidth: boolean;
  isSidebarControlledByTopbar: boolean;
  isSidebarForcedOpen: boolean;
  isSidebarOpenedByTopbar: boolean;
  isSidebarRendered: boolean;
  mode?: 'in-app' | 'navigation';
  onToggleSidebarVisibility?: () => void;
  setIsSidebarForcedOpen: (state: boolean) => void;
  setIsSidebarOpenedByTopbar: (state: boolean) => void;
  setIsSidebarRendered: (state: boolean) => void;
  shouldRenderCallToAction: boolean;
  shouldRenderSearch: boolean;
  shouldRenderSidebar: boolean;
  shouldRenderAccessibleSidebar: boolean;
  shouldRenderTopbarLinks: boolean;
  shouldRenderUniversalNav: boolean;
  showDynamicNav: boolean;
  sidebarWidth: number;
  topbarSize: 'small' | 'medium';
  topbarHeight: number;
  topNavData: TopBarLinksType;
  useQuery: UseQuery;
  urlPathPrefix?: string;
  getFullyQualifiedURL: (path?: string | null) => string | undefined;
};

const defaultContext: UniversalNavContext = {
  canForceSidebarOpen: false,
  canTopbarFitBackToAppButton: false,
  canTopbarFitLinks: false,
  currentAccountId: undefined,
  currentUrl: '',
  isLoadingLoggedIn: true,
  isLoadingTopNav: true,
  isLoggedIn: false,
  isOnAppPage: false,
  isSearchFullWidth: true,
  isSidebarControlledByTopbar: false,
  isSidebarForcedOpen: false,
  isSidebarOpenedByTopbar: false,
  isSidebarRendered: false,
  onToggleSidebarVisibility: () => {},
  setIsSidebarForcedOpen: () => {},
  setIsSidebarOpenedByTopbar: () => {},
  setIsSidebarRendered: () => {},
  shouldRenderCallToAction: false,
  shouldRenderSearch: true,
  shouldRenderSidebar: false,
  shouldRenderAccessibleSidebar: false,
  shouldRenderTopbarLinks: false,
  shouldRenderUniversalNav: true,
  showDynamicNav: false,
  sidebarWidth: 0,
  topbarSize: 'medium',
  topbarHeight: UNIVERSAL_TOPBAR_CONDENSED_HEIGHT,
  topNavData: {},
  // @ts-ignore. This is a default value.
  useQuery: () => {},
  getFullyQualifiedURL: (path?: string | null) => path || undefined,
};

export const UniversalNavContext = createContext(defaultContext);

export const useUniversalNavContext = () => useContext(UniversalNavContext);

type Props = {
  /**
   * Optionally specify the rendering mode for UniversalLayout:
   *   `in-app`: The logged-in experience including the sidebar menu.
   *   `navigation`: No sidebar, but with navigation links in the topbar.
   *
   * If omitted, the mode will be inferred from the `currentUrl` prop.
   */
  mode?: 'in-app' | 'navigation';
  /**
   * What to render within the provider.
   */
  children: React.ReactNode;
  /**
   * `id` of the current account. Will be falsey if user isn't logged in.
   */
  currentAccountId?: number;
  /**
   * Current path in the browser's address bar.
   */
  currentUrl: string;
  /**
   * Optional hostname prop that will render as absolute path instead of a relative path.
   */
  urlPathPrefix?: string;
  /**
   * Hook to actually execute the query. Typically provided by an `@apollo` package.
   */
  useQuery: UseQuery;
};

export const isAuthenticatedQuery: DocumentNode = gql`
  query isAuthenticatedQuery {
    isAuthenticatedV2
  }
`;

const isCurrentUrlAnAuthPage = (currentUrl: string) => {
  return (
    currentUrl.includes('/login') ||
    currentUrl.includes('/sign-up') ||
    currentUrl.includes('/oauth/authorize')
  );
};

const isCurrentUrlAnAppPage = (currentUrl: string, mode?: string) => {
  if (mode) {
    return mode === 'in-app' ? true : false;
  }
  return currentUrl.includes('/app/');
};

const isCurrentUrlAnOnboardingPage = (currentUrl: string) => {
  return currentUrl.includes('/app/onboarding');
};

const shouldRenderSidebarForRoute = ({
  currentUrl,
  mode,
}: {
  currentUrl: string;
  mode?: Props['mode'];
}) => {
  return (
    isCurrentUrlAnAppPage(currentUrl, mode) &&
    !isCurrentUrlAnAuthPage(currentUrl) &&
    !isCurrentUrlAnOnboardingPage(currentUrl)
  );
};

export const shouldRenderTopbarLinksForRoute = ({
  currentUrl,
  mode,
}: {
  currentUrl: string;
  mode?: Props['mode'];
}) => {
  return (
    !isCurrentUrlAnAppPage(currentUrl, mode) &&
    !isCurrentUrlAnAuthPage(currentUrl)
  );
};

const getShouldRenderSidebar = ({
  isLoggedIn,
  canTopbarFitLinks,
  currentUrl,
  isSidebarControlledByTopbar,
  isSidebarOpenedByTopbar,
  mode,
}: {
  isLoggedIn: boolean;
  canTopbarFitLinks: boolean;
  currentUrl: string;
  isSidebarControlledByTopbar: boolean;
  isSidebarOpenedByTopbar: boolean;
  mode?: Props['mode'];
}) => {
  if (typeof window === 'undefined') {
    return false;
  }
  if (isSidebarControlledByTopbar) {
    return isSidebarOpenedByTopbar;
  }
  if (mode && mode === 'navigation') {
    return canTopbarFitLinks === false ? true : false;
  }

  if (mode && mode === 'in-app' && isLoggedIn) {
    return true;
  }

  if (isLoggedIn && shouldRenderSidebarForRoute({ currentUrl, mode })) {
    return true;
  }
  if (
    !canTopbarFitLinks &&
    shouldRenderTopbarLinksForRoute({ currentUrl, mode })
  ) {
    return true;
  }
  return false;
};

const getShouldRenderAccessibleSidebar = ({
  isLoggedIn,
  canTopbarFitLinks,
  currentUrl,
  isSidebarControlledByTopbar,
  isSidebarOpenedByTopbar,
  mode,
}: {
  isLoggedIn: boolean;
  canTopbarFitLinks: boolean;
  currentUrl: string;
  isSidebarControlledByTopbar: boolean;
  isSidebarOpenedByTopbar: boolean;
  mode?: Props['mode'];
}) => {
  if (typeof window === 'undefined') {
    return false;
  }
  if (isSidebarControlledByTopbar) {
    return !isSidebarOpenedByTopbar;
  }
  if (mode && mode === 'navigation') {
    return canTopbarFitLinks === false ? true : false;
  }

  if (mode && mode === 'in-app' && isLoggedIn && canTopbarFitLinks) {
    return false;
  }

  if (
    isLoggedIn &&
    shouldRenderSidebarForRoute({ currentUrl, mode }) &&
    !canTopbarFitLinks
  ) {
    return true;
  }
  if (
    !canTopbarFitLinks &&
    shouldRenderTopbarLinksForRoute({ currentUrl, mode })
  ) {
    return true;
  }
  return false;
};

const getShouldRenderSearch = ({ currentUrl }: { currentUrl: string }) => {
  if (
    isCurrentUrlAnAuthPage(currentUrl) ||
    isCurrentUrlAnOnboardingPage(currentUrl)
  ) {
    return false;
  }
  return true;
};

const getShouldRenderTopbarToggle = ({
  isSidebarControlledByTopbar,
  isLoggedIn,
  currentUrl,
  mode,
}: {
  isSidebarControlledByTopbar: boolean;
  isLoggedIn: boolean;
  currentUrl: string;
  mode?: Props['mode'];
}) => {
  // If the sidebar isn't controlled by the topbar then
  // the toggle should never render, so bail immediately.
  if (!isSidebarControlledByTopbar) {
    return false;
  }
  if (isLoggedIn) {
    return true;
  }
  return shouldRenderTopbarLinksForRoute({
    currentUrl,
    mode,
  });
};

const getSidebarWidth = ({
  canForceSidebarOpen,
  isSidebarForcedOpen,
  isSidebarRendered,
  isSidebarControlledByTopbar,
}: {
  canForceSidebarOpen: boolean;
  isSidebarForcedOpen: boolean;
  isSidebarRendered: boolean;
  isSidebarControlledByTopbar: boolean;
}) => {
  if (!isSidebarRendered || isSidebarControlledByTopbar) {
    return 0;
  }
  if (canForceSidebarOpen && isSidebarForcedOpen) {
    return UNIVERSAL_SIDEBAR_WIDTH;
  }
  return UNIVERSAL_SIDEBAR_COLLAPSED_WIDTH;
};

export const UniversalNavProvider = (props: Props) => {
  const zapid = readCookie('zapidentity');
  const { isLoading: isLoadingTopNav, topNav } = useTopNav(zapid || '');
  const { currentUrl, useQuery, urlPathPrefix, mode } = props;
  const isOnAppPage = isCurrentUrlAnAppPage(currentUrl, mode);
  const {
    isSearchFullWidth,
    isSidebarControlledByTopbar,
    canForceSidebarOpen,
    canTopbarFitBackToAppButton,
    canTopbarFitLinks,
    topbarSize,
    topbarHeight,
  } = useMediaProps({
    '(min-width: 0px)': {
      isSidebarControlledByTopbar: true,
      isSearchFullWidth: true,
      canForceSidebarOpen: false,
      canTopbarFitBackToAppButton: false,
      canTopbarFitLinks: false,
      topbarSize: 'small',
      topbarHeight: UNIVERSAL_TOPBAR_CONDENSED_HEIGHT,
    },
    '(min-width: 450px)': {
      canTopbarFitBackToAppButton: isEmptyObject(topNav.callToAction),
      topbarSize: isEmptyObject(topNav.callToAction) ? 'medium' : 'small',
      topbarHeight: UNIVERSAL_TOPBAR_HEIGHT,
    },
    '(min-width: 650px)': {
      canTopbarFitBackToAppButton: true,
      topbarSize: 'medium',
      topbarHeight: UNIVERSAL_TOPBAR_HEIGHT,
    },

    '(min-width: 1040px)': {
      isSidebarControlledByTopbar: false,
      isSearchFullWidth: false,
      canTopbarFitLinks: true,
    },
    '(min-width: 1280px)': {
      canForceSidebarOpen: shouldRenderSidebarForRoute({ currentUrl, mode }),
    },
  });
  const shouldRenderTopbarLinks =
    canTopbarFitLinks &&
    shouldRenderTopbarLinksForRoute({
      currentUrl,
      mode,
    });
  const authResult = useQuery(isAuthenticatedQuery, {
    // Always recheck whether the user is authenticated.
    // This is especially important when transitioning from
    // being logged out to logged in and vice versa.
    fetchPolicy: 'cache-and-network',
    ssr: false,
  });
  const isLoggedIn = authResult.data && authResult.data.isAuthenticatedV2;
  const [isSidebarOpenedByTopbar, setIsSidebarOpenedByTopbar] = useState(false);
  const [isSidebarForcedOpen, setIsSidebarForcedOpen] = useLocalStorage(
    'UniversalNav:isSidebarForcedOpen'
  );
  // Default to opening sidebar if the user hasn't chosen yet.
  useEffect(() => {
    if (isSidebarForcedOpen === null || isSidebarForcedOpen === undefined) {
      setIsSidebarForcedOpen(true);
    }
  }, [isSidebarForcedOpen, setIsSidebarForcedOpen]);
  const [isSidebarRendered, setIsSidebarRendered] = useState(false);
  const shouldRenderSidebar = getShouldRenderSidebar({
    isLoggedIn,
    canTopbarFitLinks,
    currentUrl,
    isSidebarControlledByTopbar,
    isSidebarOpenedByTopbar,
    mode,
  });
  const shouldRenderAccessibleSidebar = getShouldRenderAccessibleSidebar({
    isLoggedIn,
    canTopbarFitLinks,
    currentUrl,
    isSidebarControlledByTopbar,
    isSidebarOpenedByTopbar,
    mode,
  });
  const shouldRenderSearch = getShouldRenderSearch({
    currentUrl,
  });
  const shouldRenderTopbarToggle = getShouldRenderTopbarToggle({
    isSidebarControlledByTopbar,
    isLoggedIn,
    currentUrl,
  });
  const onToggleSidebarVisibility = shouldRenderTopbarToggle
    ? () => {
        setIsSidebarOpenedByTopbar(!isSidebarOpenedByTopbar);
      }
    : undefined;
  const sidebarWidth = getSidebarWidth({
    canForceSidebarOpen,
    isSidebarForcedOpen,
    isSidebarRendered,
    isSidebarControlledByTopbar,
  });

  const memoizedFullyQualifiedUrl = useCallback(
    (link?: string | null): string | undefined => {
      if (link && urlPathPrefix && link.startsWith('/'))
        return urlPathPrefix + link;

      return link || undefined;
    },
    [urlPathPrefix]
  );

  const value = {
    currentAccountId: props.currentAccountId,
    isLoadingLoggedIn: authResult.loading,
    isLoadingTopNav,
    canTopbarFitBackToAppButton,
    canTopbarFitLinks,
    canForceSidebarOpen,
    currentUrl,
    isLoggedIn,
    isOnAppPage,
    isSearchFullWidth,
    isSidebarControlledByTopbar,
    isSidebarOpenedByTopbar,
    isSidebarForcedOpen:
      canForceSidebarOpen && isSidebarRendered ? isSidebarForcedOpen : false,
    isSidebarRendered,
    mode,
    onToggleSidebarVisibility,
    setIsSidebarOpenedByTopbar,
    setIsSidebarForcedOpen,
    setIsSidebarRendered,
    shouldRenderCallToAction: !isCurrentUrlAnOnboardingPage(currentUrl),
    shouldRenderSearch,
    shouldRenderSidebar,
    shouldRenderAccessibleSidebar,
    shouldRenderTopbarLinks,
    shouldRenderUniversalNav: true,
    showDynamicNav: !isEmptyObject(topNav),
    sidebarWidth,
    topbarHeight,
    topbarSize,
    topNavData: topNav,
    useQuery,
    urlPathPrefix,
    getFullyQualifiedURL: memoizedFullyQualifiedUrl,
  };
  return (
    <UniversalNavContext.Provider value={value}>
      {props.children}
    </UniversalNavContext.Provider>
  );
};
